/* eslint-disable @typescript-eslint/no-explicit-any */
import Api from '@sendsprint/api-types';
import { UserAxiosConfig, RequestConfig, BusinessAxiosConfig, addTokenHeader } from '@src/config';
import { BUSINESS_API_VERSION } from '@src/constants';
import ClientApi from '@src/types/client';
import { getState } from '@src/utils';
import { getDateTimeWithTimezone } from '@src/utils/date';
import { AxiosResponse } from 'axios';
import { decorateWithAppIdAndKeyAndEmailAndHash, getApiEndpointResponse } from './api-service';

const formatTransactionInfo = ({
  TxRef: txRef,
  Amount: amount,
  payment_plan,
  URL
}: ClientApi.General.ExtendedTransactionRefInfo): ClientApi.General.TransactionRefInfo => {
  return { txRef, amount, payment_plan, URL };
};

const formatTransaction = (t: Api.Model.Transaction): ClientApi.General.Transaction => {
  const createdDateISO = getDateTimeWithTimezone(t.DateCreated);
  const updatedDateISO = getDateTimeWithTimezone(t.DateUpdated);

  return {
    id: t.Id,
    txRef: t.TxRef,
    sendAmount: t.ChargedAmount - t.Fee,
    receiveAmount: t.Amount,
    rate: t.Rate,
    fee: t.Fee,
    sendCurrency: t.SourceCurrency,
    receiveCurrency: t.TargetCurrency,
    securityQuestion: t.SecurityQuestion,
    securityAnswer: t.SecurityAnswer,
    status: t.Status,
    createdDate: new Date(createdDateISO),
    createdDateISO: createdDateISO,
    updatedDate: new Date(updatedDateISO),
    updatedDateISO: updatedDateISO,
    recipientId: t.RecipientId,
    ownerId: t.OwnerId,
    recipientName: t.RecipientName
    // paymentType: t.PaymentType
  };
};

/**
 * ===================
 * ENDPOINTS
 * ===================
 */

export const createTransactionService = async (
  payload: ClientApi.Transactions.CreateTransaction.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.CreateTransaction.Request>
): Promise<ClientApi.General.TransactionRefInfo> => {
  const recurringPayload = {
    name: payload.transferScheduleData?.name,
    interval: payload.transferScheduleData?.interval,
    currency: payload.sendCurrency,
    duration: payload.transferScheduleData?.duration
  };

  const serializedData: Api.Transaction.CreateTransaction.Request =
    payload.transferScheduleData?.transferType === 'recurring'
      ? decorateWithAppIdAndKeyAndEmailAndHash({
          RecipientId: `${payload.recipientId}`,
          Amount: `${payload.amount}`,
          SourceCurrency: payload.sendCurrency,
          TargetCurrency: payload.receiveCurrency,
          SecurityQuestion: payload.securityQuestion,
          SecurityAnswer: payload.securityAnswer,
          RecurringRequest: recurringPayload,
          GiftRequest: payload.gift,
          ChargeRoute: payload.chargeRoute,
          WireFXRequest: payload.wireFxRequest,
          PIN: payload.PIN,
          ModulrBank: payload.ModulrBank,
          Voucher: payload.Voucher,
          IsBusiness: 'True'
        })
      : decorateWithAppIdAndKeyAndEmailAndHash({
          RecipientId: `${payload.recipientId}`,
          Amount: `${payload.amount}`,
          SourceCurrency: payload.sendCurrency,
          TargetCurrency: payload.receiveCurrency,
          SecurityQuestion: payload.securityQuestion,
          SecurityAnswer: payload.securityAnswer,
          GiftRequest: payload.gift,
          ChargeRoute: payload.chargeRoute,
          WireFXRequest: payload.wireFxRequest,
          PIN: payload.PIN,
          ModulrBank: payload.ModulrBank,
          Voucher: payload.Voucher,
          IsBusiness: 'True'
        });

  return UserAxiosConfig.post<
    Api.Transaction.CreateTransaction.Response,
    AxiosResponse<Api.Transaction.CreateTransaction.Response>
  >('/account/inserttransaction', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data }) => formatTransactionInfo(Data));
};

export const createBulkTransactionService = async (
  payload: ClientApi.Transactions.CreateBulkTransaction.Request
): Promise<ClientApi.Transactions.CreateBulkTransaction.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.CreateBulkTransaction.Response,
    AxiosResponse<ClientApi.Transactions.CreateBulkTransaction.Response>,
    ClientApi.Transactions.CreateBulkTransaction.Request
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/InsertBulkTransfer`, payload, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const createPaymentLinkTransactionService = async (
  payload: ClientApi.Transactions.CreatePaymentLinkTransaction.Request
): Promise<ClientApi.Transactions.CreatePaymentLinkTransaction.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.CreatePaymentLinkTransaction.Response,
    AxiosResponse<ClientApi.Transactions.CreatePaymentLinkTransaction.Response>,
    ClientApi.Transactions.CreatePaymentLinkTransaction.Request
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/InsertPaymentLinkTransfer`, payload, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const cancelTransactionService = async (
  payload: ClientApi.Transactions.CancelTransaction.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig
): Promise<ClientApi.Transactions.CancelTransaction.Response['Data']> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    ...payload
  });

  return UserAxiosConfig.post<
    ClientApi.Transactions.CancelTransaction.Response,
    AxiosResponse<ClientApi.Transactions.CancelTransaction.Response>
  >('/account/canceltransaction', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data }) => Data);
};

export const cancelBulkTransactionService = async (
  payload: ClientApi.Transactions.CancelBulkTransaction.Request
): Promise<ClientApi.Transactions.CancelBulkTransaction.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.Transactions.CancelBulkTransaction.Response,
    AxiosResponse<ClientApi.Transactions.CancelBulkTransaction.Response>,
    ClientApi.Transactions.CancelBulkTransaction.Request
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/CancelTransaction`, payload, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getTransactionsByTxrefService = async (
  payload: ClientApi.Transactions.GetTransactionByTxref.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.GetTransactionByTxref.Request>
): Promise<null | ClientApi.General.Transaction> => {
  const serializedData: Api.Transaction.FindTransactionByRef.Request =
    decorateWithAppIdAndKeyAndEmailAndHash({ TxRef: payload.txref });

  return UserAxiosConfig.post<
    Api.Transaction.FindTransactionByRef.Response,
    AxiosResponse<Api.Transaction.FindTransactionByRef.Response>
  >('/account/loadtransactionbytxref', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data: data }) => data && formatTransaction(data[0]));
};

export const checkIfPinExistsService = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig
): Promise<ClientApi.Transactions.CheckIfPinExists.Response> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({});

  return UserAxiosConfig.post<
    ClientApi.Transactions.CheckIfPinExists.Response,
    AxiosResponse<ClientApi.Transactions.CheckIfPinExists.Response>
  >('/account/checkifpinexists', serializedData, config).then(getApiEndpointResponse);
};

export const createPinService = async (
  payload: ClientApi.Transactions.CreatePin.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig
): Promise<ClientApi.Transactions.CreatePin.Response> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    PIN: payload.PIN
  });

  return UserAxiosConfig.post<
    ClientApi.Transactions.CreatePin.Response,
    AxiosResponse<ClientApi.Transactions.CreatePin.Response>
  >('/account/createpin', serializedData, config).then(getApiEndpointResponse);
};

export const changePinService = async (
  payload: ClientApi.Transactions.ChangePin.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig
): Promise<ClientApi.Transactions.ChangePin.Response['Data']> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    OldPIN: payload.OldPIN,
    NewPIN: payload.NewPIN,
    Password: payload.Password
  });

  return UserAxiosConfig.post<
    ClientApi.Transactions.ChangePin.Response,
    AxiosResponse<ClientApi.Transactions.ChangePin.Response>
  >('/account/changepin', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data }) => Data);
};

export const resetPinService = async (
  payload: ClientApi.Transactions.ResetPin.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig
): Promise<ClientApi.Transactions.ResetPin.Response['Data']> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    OTPCode: payload.OTPCode,
    NewPIN: payload.NewPIN,
    Password: payload.Password
  });

  return UserAxiosConfig.post<
    ClientApi.Transactions.ResetPin.Response,
    AxiosResponse<ClientApi.Transactions.ResetPin.Response>
  >('/account/resetpin', serializedData, config)
    .then(getApiEndpointResponse)
    .then(({ Data }) => Data);
};

export const getBulkTransactionsService = async (
  payload: ClientApi.Transactions.GetBulkTransactions.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetBulkTransactions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload?.startDate,
      endDate: payload?.endDate
    },
    searchValue: payload.searchValue
  };

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetBulkTransactions.Response,
    AxiosResponse<ClientApi.Transactions.GetBulkTransactions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetBulkTransactionsSummary`, payload2, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getBulkTransactionItemsService = async (
  payload: ClientApi.Transactions.GetBulkTransactionItems.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetBulkTransactionItems.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetBulkTransactionItems.Response,
    AxiosResponse<ClientApi.Transactions.GetBulkTransactionItems.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetBulkTransactionItems`, payload, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getSingleTransactionsService = async (
  payload: ClientApi.Transactions.GetSingleTransactions.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetSingleTransactions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload?.startDate,
      endDate: payload?.endDate
    },
    searchValue: payload.searchValue,
    transactionType: payload?.transactionType
  };

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetSingleTransactions.Response,
    AxiosResponse<ClientApi.Transactions.GetSingleTransactions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetTransactions`, payload2, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getSingleTransactionByReferenceService = async (
  payload: ClientApi.Transactions.GetSingleTransactionByReference.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetSingleTransactionByReference.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');

  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetSingleTransactionByReference.Response,
    AxiosResponse<ClientApi.Transactions.GetSingleTransactionByReference.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetTransactionItem`, payload, {
    ...config,
    ...configWithToken
  })
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const loadVoucherService = async (
  payload: ClientApi.Transactions.LoadVoucher.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.LoadVoucher.Request>
): Promise<any> => {
  if (!payload.voucher) return null;

  return UserAxiosConfig.get(`/account/loadvoucher/${payload.voucher}`, config).then(
    getApiEndpointResponse
  );
};

export const loadPaymentCodeService = async (
  payload: ClientApi.Transactions.LoadPaymentCode.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.LoadPaymentCode.Request>
): Promise<ClientApi.Transactions.LoadPaymentCode.PaymentCodeI[]> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    Code: payload.code
  });

  return UserAxiosConfig.post<
    ClientApi.Transactions.LoadPaymentCode.Response,
    AxiosResponse<ClientApi.Transactions.LoadPaymentCode.Response>
  >('/account/loadpaymentcode', serializedData, config)
    .then(getApiEndpointResponse)
    .then((res) => res.Data);
};

export const nethoneCheckByTxrefService = async (
  payload: ClientApi.Transactions.NethoneCheckByTxref.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.NethoneCheckByTxref.Request>
): Promise<any> => {
  const serializedData = decorateWithAppIdAndKeyAndEmailAndHash({
    TxRef: payload.txref
  });

  return UserAxiosConfig.post<any, AxiosResponse<any>>(
    '/account/nethonecheckbytxref',
    serializedData,
    config
  ).then(getApiEndpointResponse);
};

export const nethoneCheckByTxrefPublicService = async (
  payload: ClientApi.Transactions.NethoneCheckByTxref.Request,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config?: RequestConfig<ClientApi.Transactions.NethoneCheckByTxref.Request>
): Promise<any> => {
  return UserAxiosConfig.get<any, AxiosResponse<any>>(
    `/testemail/nethonecheckbytxref?txRef=${payload.txref}`,
    config
  ).then(getApiEndpointResponse);
};

export const getSenderTransactionSummaryService = (
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetSenderTransactionSummary.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Transactions.GetSenderTransactionSummary.Response,
    AxiosResponse<ClientApi.Transactions.GetSenderTransactionSummary.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/SenderTransactionSummary`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getPaymentLinkTransactionsService = (
  payload: ClientApi.Transactions.GetPaymentLinkTransactions.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetPaymentLinkTransactions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    linkReference: payload.linkReference,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload.startDate,
      endDate: payload.endDate
    },
    searchValue: payload.searchValue
  };

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetPaymentLinkTransactions.Response,
    AxiosResponse<ClientApi.Transactions.GetPaymentLinkTransactions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/PaymentLinkTransactions`, payload2, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getReceiverTransactionsService = (
  payload: ClientApi.Transactions.GetReceiverTransactions.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetReceiverTransactions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload.startDate,
      endDate: payload.endDate
    },
    searchValue: payload.searchValue,
    transactionType: payload.transactionType,
    isSettled: payload.isSettled
  };

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetReceiverTransactions.Response,
    AxiosResponse<ClientApi.Transactions.GetReceiverTransactions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetReceiverTransactions`, payload2, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getSettlementsAccountTransactionsService = (
  payload: ClientApi.Transactions.GetSettlementAccountTransactions.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetSettlementAccountTransactions.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const payload2 = {
    pageNumber: payload.pageNumber,
    pageSize: payload.pageSize,
    periodSelector: payload.periodSelector,
    dateRange: {
      startDate: payload.startDate,
      endDate: payload.endDate
    },
    searchValue: payload.searchValue,
    accountNumber: payload.accountNumber
  };

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.post<
    ClientApi.Transactions.GetSettlementAccountTransactions.Response,
    AxiosResponse<ClientApi.Transactions.GetSettlementAccountTransactions.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetSettlementAccountTransactions`, payload2, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getReceiverTransactionSummaryService = (
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetReceiverTransactionSummary.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Transactions.GetReceiverTransactionSummary.Response,
    AxiosResponse<ClientApi.Transactions.GetReceiverTransactionSummary.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/ReceiverTransactionSummary`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getReceiverTransactionAmountsSummaryService = (
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetReceiverTransactionAmountsSummary.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Transactions.GetReceiverTransactionAmountsSummary.Response,
    AxiosResponse<ClientApi.Transactions.GetReceiverTransactionAmountsSummary.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/ReceiverTransactionAmountsSummary`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data?.data);
};

export const getPaymentLinkUsageService = (
  payload: ClientApi.Transactions.GetPaymentLinkUsage.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetPaymentLinkUsage.Response> => {
  const state = getState();
  const token = state?.tokens?.token.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Transactions.GetPaymentLinkUsage.Response,
    AxiosResponse<ClientApi.Transactions.GetPaymentLinkUsage.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/Transfer/GetPaymentLinkUsage?paymentLinkId=${payload.paymentLinkId}`,
    {
      ...config,
      ...configWithToken
    }
  ).then((res) => res.data);
};

export const getPaymentSummaryCountService = (
  payload: ClientApi.Transactions.GetPaymentSummaryCount.Request,
  config?: RequestConfig
): Promise<ClientApi.Transactions.GetPaymentSummaryCount.Response> => {
  const state = getState();
  const token = state?.tokens?.token.accessToken;

  const configWithToken = addTokenHeader(token || '');
  return BusinessAxiosConfig.get<
    ClientApi.Transactions.GetPaymentSummaryCount.Response,
    AxiosResponse<ClientApi.Transactions.GetPaymentSummaryCount.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Transfer/GetPaymentSummaryCount?currency=${payload.currency}`, {
    ...config,
    ...configWithToken
  }).then((res) => res.data);
};
